import * as React from "react";
import { useEffect, useRef } from "react";
import * as d3 from "d3";
import "d3-transition";
import { busService } from "@modules/bus";
const WINDOW_SECONDS = 60;
const UPDATE_INTERVAL_MS = 100;
const WINDOW_POINTS = (WINDOW_SECONDS * 1000) / UPDATE_INTERVAL_MS;
const UPDATES_PER_SECOND = 1000 / UPDATE_INTERVAL_MS;
const HEIGHT = 100;
const MARGIN = { top: 15, right: 20, bottom: 17, left: 35 };
export function MessageRateChart({ enabled }) {
    const svgRef = useRef(null);
    const dataRef = useRef(createInitialData());
    const elapsedRef = useRef(WINDOW_SECONDS);
    const counterRef = useRef(0);
    const typeCountsRef = useRef({});
    useEffect(() => {
        const connection = busService.connection("MessageRateChart");
        const handler = connection.subscribe("*", (message, meta) => {
            var _a;
            if (meta.source.moduleId === "MessageRateChart")
                return;
            counterRef.current++;
            const type = message.type;
            typeCountsRef.current[type] = ((_a = typeCountsRef.current[type]) !== null && _a !== void 0 ? _a : 0) + 1;
        });
        return () => {
            connection.unsubscribe(handler);
        };
    }, [enabled]);
    useEffect(() => {
        const svgElement = svgRef.current;
        if (svgElement === null)
            return;
        const containerElement = svgElement.parentElement;
        if (containerElement === null)
            return;
        const svg = d3.select(svgElement);
        const width = svgElement.clientWidth;
        const innerWidth = width - MARGIN.left - MARGIN.right;
        const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
        svg.selectAll("*").remove();
        const g = svg
            .append("g")
            .attr("transform", `translate(${MARGIN.left},${MARGIN.top})`);
        const x = d3.scaleLinear().range([0, innerWidth]);
        const y = d3.scaleLinear().range([innerHeight, 0]);
        const line = d3.line()
            .x(d => x(d.time))
            .y(d => y(d.value))
            .curve(d3.curveMonotoneX);
        const path = g.append("path")
            .attr("fill", "none")
            .attr("stroke", "#4da3ff")
            .attr("stroke-width", 2);
        const xAxis = g.append("g")
            .attr("transform", `translate(0,${innerHeight})`);
        const yAxis = g.append("g");
        // ----- Hover elements -----
        const hoverLine = g.append("line")
            .attr("stroke", "#888")
            .attr("stroke-dasharray", "4 4")
            .style("display", "none");
        const container = d3.select(containerElement);
        const tooltip = container
            .append("div")
            .style("position", "absolute")
            .style("background", "#111")
            .style("color", "#fff")
            .style("padding", "4px 8px")
            .style("font-size", "12px")
            .style("border-radius", "4px")
            .style("pointer-events", "none")
            .style("opacity", 0)
            .style("z-index", 9999);
        const overlay = g.append("rect")
            .attr("width", innerWidth)
            .attr("height", innerHeight)
            .attr("fill", "transparent")
            .style("pointer-events", "all");
        overlay
            .on("mousemove", (event) => {
            const [mx] = d3.pointer(event);
            const time = x.invert(mx);
            const data = dataRef.current;
            if (data.length === 0)
                return;
            const closest = data.reduce((prev, curr) => Math.abs(curr.time - time) < Math.abs(prev.time - time)
                ? curr
                : prev);
            hoverLine
                .style("display", null)
                .attr("x1", 0)
                .attr("x2", innerWidth)
                .attr("y1", y(closest.value))
                .attr("y2", y(closest.value));
            // Prevent tooltip to overflow on the right edge of the plot
            const tooltipNode = tooltip.node();
            if (tooltipNode === null)
                return;
            const containerNode = containerElement;
            const tooltipWidth = tooltipNode.offsetWidth;
            const xPos = MARGIN.left + x(closest.time);
            const containerWidth = containerNode.clientWidth;
            // Clamp horizontally
            let left = xPos - tooltipWidth / 2;
            if (left + tooltipWidth > containerWidth) {
                left = xPos - tooltipWidth - 12;
            }
            const tooltipHeight = tooltipNode.offsetHeight;
            const top = -tooltipHeight - 12;
            tooltip
                .style("opacity", 1)
                .html(buildTooltipHtml(closest))
                .style("left", `${left}px`)
                .style("top", `${top}px`);
        })
            .on("mouseleave", () => {
            hoverLine.style("display", "none");
            tooltip.style("opacity", 0);
        });
        // ----- Update Loop -----
        const update = () => {
            var _a;
            elapsedRef.current += 1 / UPDATES_PER_SECOND;
            const count = counterRef.current;
            counterRef.current = 0;
            dataRef.current.push({
                time: elapsedRef.current,
                value: count,
                typeCounts: typeCountsRef.current
            });
            typeCountsRef.current = {};
            if (dataRef.current.length > WINDOW_POINTS) {
                dataRef.current.shift();
            }
            const data = dataRef.current;
            const maxTime = elapsedRef.current;
            const minTime = maxTime - WINDOW_SECONDS;
            x.domain([minTime, maxTime]);
            y.domain([0, Math.max(1, (_a = d3.max(data, d => d.value)) !== null && _a !== void 0 ? _a : 1)]).nice();
            path
                .datum(data)
                .attr("d", line);
            xAxis
                .call(d3.axisBottom(x)
                .ticks(6)
                .tickFormat(d => `${Number(d).toFixed(0)}s`));
            yAxis
                .call(d3.axisLeft(y).ticks(5));
        };
        const interval = setInterval(update, UPDATE_INTERVAL_MS);
        return () => {
            clearInterval(interval);
            tooltip.remove();
        };
    }, []);
    return (React.createElement("div", { className: "chart-container" },
        React.createElement("svg", { ref: svgRef, width: "100%", height: HEIGHT })));
}
function createInitialData() {
    const data = [];
    for (let i = 0; i < WINDOW_POINTS; i++) {
        data.push({
            time: (i + 1) / UPDATES_PER_SECOND,
            value: 0,
            typeCounts: {}
        });
    }
    return data;
}
function buildTooltipHtml(dataPoint) {
    const rows = Object.entries(dataPoint.typeCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([type, count]) => `<tr><td style="padding:0 12px 0 0;">${escapeHtml(type)}</td><td style="text-align:right;">${formatPercentage(count, dataPoint.value)}</td></tr>`)
        .join("");
    const details = rows.length > 0
        ? `<table style="margin-top:4px;border-collapse:collapse;">${rows}</table>`
        : "";
    return `Time: ${dataPoint.time.toFixed(1)}s<br/>Messages: ${dataPoint.value}${details}`;
}
function formatPercentage(count, total) {
    if (total <= 0)
        return "0%";
    const percentage = (count / total) * 100;
    const rounded = Math.round(percentage * 10) / 10;
    return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
}
function escapeHtml(value) {
    return value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll("\"", "&quot;")
        .replaceAll("'", "&#39;");
}
